import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PORTAL_COTACOES_HABILITADO,
  fetchCotacoes,
  fetchItensEmLote,
  registrarAvisoComplemento,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { COTACAO_STATUS_LABEL, CotacaoStatus } from '../../../interfaces/PortalCotacoes';
import { DiffResumo } from '../../../interfaces/OrcamentoCilia';
import { useAlcanca } from '../../../hooks/usePapeis';
import { cadeiaDaSolicitacao, estaEmAndamento } from '../../portal_cotacoes/cadeiaSolicitacao';
import { PORTAL_COTACAO_DETALHE } from '../../portal_cotacoes/routes';

/**
 * Peça que o complemento TIROU (ou reduziu) e que ainda está numa cotação em andamento no
 * Portal de Cotações — item 20 do backlog do Portal, 13/08/2026.
 *
 * **O problema real:** o orçamento seguinte tira uma peça (ou baixa a quantidade) e nada
 * avisa a cotação já enviada. O comprador fecha a cesta e gera pedido de algo que o
 * orçamento não pede mais.
 *
 * **Por que o aviso mora AQUI e não no Portal** (decisão do Victor, 13/08/2026): a
 * solicitação que o Portal recebe carrega apenas o ACRÉSCIMO de cada complemento — remoção e
 * redução não existem nela. Essa informação só existe no diff do orçamento, que é desta
 * tela. O Portal ganhou um aviso genérico de reforço; a detecção é esta.
 *
 * **Orquestração pelo front, sem S2S**: a SPA já tem sessão nos dois módulos (ADR 0014), e é
 * ela que cruza os dois lados — nenhuma rota nova entre os backends.
 *
 * ⚠️ **Falha de consulta NUNCA vira silêncio**: sem resposta, a faixa diz que não foi
 * possível verificar. Não renderizar nada equivaleria a afirmar "não há cotação em
 * andamento com estas peças", que é conclusão de negócio tirada de um erro de rede — o
 * defeito que a regra do at-60 existe para impedir.
 *
 * ---
 *
 * **at-06 (17/08/2026) — o aviso deixou de depender de alguém abrir esta tela.** Antes, a
 * detecção só existia aqui: aparecia quando o ORÇAMENTISTA abria a conferência, enquanto o
 * risco real é o COMPRADOR fechar a cesta antes disso. Agora, detectada a colisão, a própria
 * tela **registra o aviso no centro de notificações do Portal** para o comprador da cotação
 * (`POST /notificacoes/aviso-complemento`, com o JWT do usuário — caminho aprovado pelo
 * Victor em 16/08/2026; segue sem S2S).
 *
 * Três regras que vieram com isso:
 *
 * 1. **Só quem opera registra**: ORCAMENTO e ADMIN. **TI não** — ele enxerga as telas em
 *    leitura (ADR 0012) e gravar aviso é ação. O backend impõe pelo `@Roles`; aqui é para
 *    não disparar chamada que voltaria 403.
 * 2. **O resultado do registro aparece na faixa**, nunca em silêncio: falhou, a faixa mantém
 *    a orientação de avisar à mão. Dizer "o comprador foi avisado" sem ter conseguido
 *    registrar seria o falso estado feliz que a regra do at-60 proíbe.
 * 3. **O número da cotação só é link para quem alcança o detalhe do Portal** (COMPRA/ADMIN/
 *    TI): para ORCAMENTO o `RoleGuard` daquela rota nega, e um link que leva a "acesso
 *    negado" é pior que texto simples. Abrir aquela tela ao orçamentista é a decisão 1 do
 *    contrato da ponte P1→P2, e vem com ela.
 */

interface Props {
  /** `numeroCilia` do orçamento — a cadeia é `SOL-<numeroCilia>`. */
  numeroCilia: string;
  /** `versaoCilia` do complemento — identifica no aviso QUAL versão mexeu nas peças. */
  versaoCilia: number;
  diffResumo: DiffResumo;
}

interface PecaAfetada {
  /** Chave de cruzamento: o código do ERP, com o do Cilia como fallback. */
  chave: string;
  descricao: string;
  /** Rótulo exibido na faixa desta tela. */
  motivo: string;
  /**
   * As duas metades do que vai no aviso do Portal. Separadas do `motivo` de propósito: o
   * backend monta o texto a partir de dado estruturado (o cliente não envia texto pronto),
   * então a frase da tela não serve como payload.
   */
  motivoTipo: 'REMOVIDA' | 'REDUZIDA';
  detalhe?: string;
}

interface Achado {
  cotacaoId: string;
  cotacaoNumero: string;
  status: CotacaoStatus;
  pecas: PecaAfetada[];
}

/**
 * As peças que saíram ou encolheram, na chave que o Portal entende.
 * `codigoNasajon ?? codigoCilia` é exatamente a derivação que a solicitação usa para o
 * `codigoProduto` — é o que faz diff antigo (sem `codigoNasajon`) continuar casando.
 */
function pecasAfetadas(diff: DiffResumo): PecaAfetada[] {
  return [
    ...diff.removidos.map((r) => ({
      chave: r.codigoNasajon ?? r.codigoCilia,
      descricao: r.descricao,
      motivo: 'removida do orçamento',
      motivoTipo: 'REMOVIDA' as const,
    })),
    ...diff.quantidadeReduzida.map((r) => ({
      chave: r.codigoNasajon ?? r.codigoCilia,
      descricao: r.descricao,
      motivo: `quantidade reduzida de ${r.qtdAnterior} para ${r.qtdNova}`,
      motivoTipo: 'REDUZIDA' as const,
      detalhe: `de ${r.qtdAnterior} para ${r.qtdNova}`,
    })),
  ];
}

const AvisoCotacaoAberta: React.FC<Props> = ({ numeroCilia, versaoCilia, diffResumo }) => {
  const [achados, setAchados] = useState<Achado[] | null>(null);
  const [falhou, setFalhou] = useState(false);
  /**
   * Como terminou o registro do aviso ao comprador:
   * `nao-tentado` (papel de leitura, ou nada a avisar) · `registrado` · `falhou`.
   * A faixa muda de texto conforme isso — é o que impede o falso "o comprador foi avisado".
   */
  const [registro, setRegistro] = useState<'nao-tentado' | 'registrado' | 'falhou'>('nao-tentado');

  // Quem OPERA a conferência registra o aviso; TI vê a tela em leitura (ADR 0012) e o
  // backend recusaria a chamada dele com 403.
  // 🔴 Pelo CONJUNTO de papéis (ADR 0044), nunca por `userType ===` — ver a nota em
  // `OrcamentoCiliaList.tsx`.
  const podeAvisar = useAlcanca(['ORCAMENTO', 'ADMIN']);
  const alcancaDetalhe = useAlcanca(['COMPRA', 'ADMIN', 'TI']);

  const pecas = pecasAfetadas(diffResumo);

  useEffect(() => {
    // Módulo desligado (env ausente) ou nada removido/reduzido: não há o que verificar.
    if (!PORTAL_COTACOES_HABILITADO || pecas.length === 0) return;

    let ativo = true;
    (async () => {
      try {
        const cadeia = `SOL-${numeroCilia}`;
        const { data } = await fetchCotacoes({ search: cadeia, pageSize: 100 });
        /*
         * O `search` do backend é `contains`, então `SOL-9100` também casaria `SOL-91001`:
         * o filtro exato de cadeia é feito aqui, com o mesmo helper do wizard — uma regra
         * de cadeia só no sistema inteiro.
         */
        const daCadeia = data.filter(
          (c) => estaEmAndamento(c) && cadeiaDaSolicitacao(c.solicitacaoNumero) === cadeia,
        );
        if (!ativo) return;
        if (daCadeia.length === 0) {
          setAchados([]);
          return;
        }

        const { data: lotes } = await fetchItensEmLote(daCadeia.map((c) => c.id));
        if (!ativo) return;

        const encontrados: Achado[] = [];
        for (const lote of lotes) {
          const codigos = new Set(lote.itens.map((i) => i.codigoProduto));
          const afetadas = pecas.filter((p) => codigos.has(p.chave));
          if (afetadas.length > 0) {
            encontrados.push({
              cotacaoId: lote.cotacaoId,
              cotacaoNumero: lote.numero,
              status: lote.status,
              pecas: afetadas,
            });
          }
        }
        setAchados(encontrados);
        setFalhou(false);

        /*
         * Registra o aviso para o comprador de CADA cotação afetada (at-06). Uma chamada por
         * cotação: o destinatário é o comprador dela, e o dedupe do backend é por cotação +
         * conteúdo.
         *
         * `allSettled` e não `all`: uma cotação que falhe não pode impedir o aviso das
         * outras. O estado só vira `registrado` se TODAS passaram — parcial conta como falha
         * para a faixa manter a orientação de avisar à mão, que é a saída segura.
         */
        if (encontrados.length > 0 && podeAvisar) {
          const resultados = await Promise.allSettled(
            encontrados.map((a) =>
              registrarAvisoComplemento({
                cotacaoId: a.cotacaoId,
                orcamentoNumero: numeroCilia,
                orcamentoVersao: versaoCilia,
                pecas: a.pecas.map((p) => ({
                  codigo: p.chave,
                  descricao: p.descricao,
                  motivo: p.motivoTipo,
                  ...(p.detalhe ? { detalhe: p.detalhe } : {}),
                })),
              }),
            ),
          );
          if (!ativo) return;
          setRegistro(
            resultados.every((r) => r.status === 'fulfilled') ? 'registrado' : 'falhou',
          );
        }
      } catch {
        if (!ativo) return;
        // Ver o cabeçalho: silêncio aqui seria afirmar que não há cotação afetada.
        setAchados(null);
        setFalhou(true);
      }
    })();
    return () => {
      ativo = false;
    };
    // `pecas` é derivado de `diffResumo` a cada render — a dependência é a origem, não ele.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeroCilia, versaoCilia, diffResumo, podeAvisar]);

  if (!PORTAL_COTACOES_HABILITADO || pecas.length === 0) return null;

  if (falhou) {
    return (
      <div className="cilia-aviso-cotacao cilia-aviso-cotacao--indisponivel">
        Não foi possível verificar se há cotação de compra em andamento com as peças que saíram deste
        complemento. Confira no Portal de Cotações antes que a cesta seja fechada.
      </div>
    );
  }

  // Ainda carregando, ou conferido e nada afetado: nada a dizer.
  if (!achados || achados.length === 0) return null;

  /*
   * O detalhe da cotação no Portal é COMPRA/ADMIN/TI (`RoleGuard` do App.tsx). Para
   * ORCAMENTO o link levaria a "acesso negado" — pior que texto simples —, então o número
   * aparece sem link para quem não alcança a tela.
   *
   * ⚠️ `alcancaDetalhe` é calculado no topo do componente: é um hook, e aqui já passamos por
   * `return null` condicional.
   */

  return (
    <div className="cilia-aviso-cotacao">
      <strong>Atenção: há cotação de compra em andamento com peças que saíram deste complemento.</strong>
      <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
        {achados.map((a) => (
          <li key={a.cotacaoId} style={{ fontSize: 14 }}>
            {alcancaDetalhe ? (
              <Link to={PORTAL_COTACAO_DETALHE(a.cotacaoId)}>{a.cotacaoNumero}</Link>
            ) : (
              <strong>{a.cotacaoNumero}</strong>
            )}{' '}
            ({COTACAO_STATUS_LABEL[a.status]}):{' '}
            {a.pecas.map((p) => `${p.descricao} (${p.motivo})`).join('; ')}
          </li>
        ))}
      </ul>
      {/*
        O que a tela diz depende de o aviso ter sido REGISTRADO (at-06). Afirmar que o
        comprador foi avisado sem ter conseguido gravar seria o falso estado feliz que a
        regra do at-60 proíbe; e quem não registra (TI) continua vendo a orientação manual.
      */}
      {registro === 'registrado' ? (
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          Quem está cotando já foi avisado no Portal de Cotações. Se a compra for urgente, confirme
          diretamente com a pessoa antes do fechamento da cesta.
        </p>
      ) : (
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          Avise quem está cotando antes do fechamento da cesta — o pedido sairia com peça que o
          orçamento não pede mais.
          {registro === 'falhou'
            ? ' O aviso automático no Portal de Cotações não pôde ser registrado agora, então este contato precisa ser feito por você.'
            : ''}
        </p>
      )}
    </div>
  );
};

export default AvisoCotacaoAberta;
